import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { isSuperAdmin } from "../hooks/useSuperAdmin";
import { toast } from "sonner";
import UserAutocomplete from "../components/UserAutocomplete";
import ShareButton from "../components/ShareButton";
import MobileSkeleton from "../components/MobileSkeleton";

const FUNNEL_STAGES = ["New", "Engaged", "Pitched", "Negotiation", "Won", "Lost", "On Hold"];
const STAGE_COLORS = {
  New: "#64748B", Engaged: "#F59E0B", Pitched: "#8B5CF6",
  Negotiation: "#EC4899", Won: "#22C55E", Lost: "#6B7280", "On Hold": "#94A3B8",
  // legacy stages still colored if they appear
  Cold: "#64748B", Warm: "#F59E0B", Hot: "#EF4444", Qualified: "#8B5CF6",
  "RFP Received": "#06B6D4", "Proposal Sent": "#3B82F6", "Follow-up": "#F97316",
};

const fmt = (v) => {
  if (!v && v !== 0) return "-";
  if (Math.abs(v) >= 100) return `\u20B9${(v / 100).toFixed(1)}Cr`;
  return `\u20B9${v.toFixed(0)}L`;
};

const relativeTime = (dateStr) => {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const daysInStage = (lead) => {
  if (!lead?.stage_changed_at) return null;
  return Math.floor((Date.now() - new Date(lead.stage_changed_at).getTime()) / 86400000);
};

const nextStage = (current) => {
  const idx = FUNNEL_STAGES.indexOf(current);
  if (idx === -1 || idx >= FUNNEL_STAGES.length - 1) return null;
  // skip Lost and On Hold as "next"
  const next = FUNNEL_STAGES[idx + 1];
  if (next === "Lost" || next === "On Hold") return null;
  return next;
};

// --- Inline styles matching dark design system ---
const s = {
  page: { maxWidth: 900, margin: "0 auto", padding: "0 0 60px" },
  back: { background: "none", border: "none", color: "var(--t3)", cursor: "pointer", fontSize: 13, padding: "4px 0", marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 4 },
  h1: { fontFamily: "var(--font-orb)", fontSize: 22, fontWeight: 700, color: "var(--t1)", letterSpacing: ".04em", margin: 0 },
  meta: { fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap", alignItems: "center" },
  metaLink: { color: "var(--c)", textDecoration: "none" },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" },
  actions: { display: "flex", gap: 6, alignItems: "center" },
  actionBtn: { padding: "5px 12px", background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 6, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--c)", cursor: "pointer", letterSpacing: ".04em", display: "flex", alignItems: "center", gap: 4 },
  stageRow: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 16 },
  badge: (color) => ({ fontFamily: "var(--font-mono)", fontSize: 11, padding: "3px 10px", borderRadius: 20, background: color + "20", color, fontWeight: 600, letterSpacing: ".04em" }),
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 16 },
  kpiCard: { background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 10, padding: "12px 14px" },
  kpiLabel: { fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", letterSpacing: ".08em", marginBottom: 4 },
  kpiValue: { fontFamily: "var(--font-orb)", fontSize: 18, color: "var(--t1)", fontWeight: 700 },
  kpiSub: { fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", marginTop: 2 },
  section: { marginTop: 24 },
  sectionTitle: { fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", letterSpacing: ".1em", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" },
  card: { background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 10, padding: "12px 16px", marginBottom: 6 },
  smallBtn: { background: "none", border: "1px solid var(--bdr)", borderRadius: 6, padding: "4px 10px", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--c)", cursor: "pointer" },
  input: { background: "var(--bg)", border: "1px solid var(--bdr)", borderRadius: 8, padding: "8px 12px", fontFamily: "var(--font-sans)", fontSize: 12, color: "var(--t1)", outline: "none", width: "100%" },
  dot: (color) => ({ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }),
  avatar: (name) => {
    const initials = (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
    const hue = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
    return { width: 28, height: 28, borderRadius: "50%", background: `hsl(${hue}, 50%, 30%)`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-mono)", fontSize: 11, color: "#fff", fontWeight: 600, flexShrink: 0, content: initials };
  },
  tab: (active) => ({ padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".04em", background: active ? "var(--cd)" : "transparent", color: active ? "var(--c)" : "var(--t3)" }),
  copyBtn: { padding: "4px 10px", borderRadius: 6, border: "1px solid var(--bdr)", background: "var(--s2)", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t2)", cursor: "pointer" },
  select: { background: "var(--bg)", border: "1px solid var(--bdr)", borderRadius: 6, padding: "4px 8px", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t1)", outline: "none", cursor: "pointer" },
};

export default function BizDevLeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [lead, setLead] = useState(null);
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);

  // contacts
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", designation: "", email: "", phone: "", linkedin_url: "" });

  // activity / comments
  const [comment, setComment] = useState("");

  // AMP Intel
  const [researching, setResearching] = useState(false);

  // outreach tab
  const [outreachTab, setOutreachTab] = useState("whatsapp");

  // seasonal events
  const [events, setEvents] = useState([]);

  // AI insight collapsed
  const [showInsight, setShowInsight] = useState(false);

  // stage dropdown
  const [showStageDropdown, setShowStageDropdown] = useState(false);

  // more menu
  const [showMore, setShowMore] = useState(false);

  // lead editing
  const [editingLead, setEditingLead] = useState(false);
  const [editLeadForm, setEditLeadForm] = useState({});

  // tasks linked to this lead
  const [tasks, setTasks] = useState([]);

  // --- Data loading ---
  const loadData = useCallback(async () => {
    try {
      const [leadRes, briefsRes, tasksRes] = await Promise.all([
        api.get(`/leads/${leadId}`),
        api.get(`/leads/${leadId}/briefs`),
        api.get(`/leads/${leadId}/tasks`).catch(() => ({ data: [] })),
      ]);
      setLead(leadRes.data);
      setBriefs(briefsRes.data?.briefs || []);
      setTasks(tasksRes.data || []);
    } catch (e) {
      toast.error("Failed to load lead");
      navigate("/bizdev/leads");
    } finally {
      setLoading(false);
    }
  }, [leadId, navigate]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Redirect Won leads to account page
  useEffect(() => {
    if (lead && lead.funnel_stage === "Won") {
      navigate(`/bizdev/accounts/${leadId}`, { replace: true });
    }
  }, [lead, leadId, navigate]);

  // Load seasonal events
  useEffect(() => {
    if (lead?.industry) {
      api.get(`/seasonal-events?industry=${encodeURIComponent(lead.industry)}`)
        .then(res => setEvents(res.data || []))
        .catch(() => {});
    }
  }, [lead?.industry]);

  // --- Handlers ---
  const startEditLead = () => {
    setEditLeadForm({
      company_name: lead.company_name || "",
      industry: lead.industry || "",
      location: lead.location || "",
      website: lead.website || "",
      is_retainer: lead.is_retainer || false,
      estimated_value_lakhs: lead.estimated_value_lakhs || "",
    });
    setEditingLead(true);
  };

  const saveLeadEdit = async () => {
    try {
      const payload = { ...editLeadForm };
      if (payload.estimated_value_lakhs) payload.estimated_value_lakhs = parseFloat(payload.estimated_value_lakhs);
      await api.put(`/leads/${leadId}`, payload);
      toast.success("Lead updated");
      setEditingLead(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const handleStageChange = async (stage) => {
    try {
      const res = await api.put(`/leads/${leadId}/stage`, { stage });
      setLead(res.data);
      toast.success(`Moved to ${stage}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setShowStageDropdown(false);
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim()) return;
    try {
      const res = await api.post(`/leads/${leadId}/contacts`, contactForm);
      setLead(res.data);
      setShowAddContact(false);
      setContactForm({ name: "", designation: "", email: "", phone: "", linkedin_url: "" });
      toast.success("Contact added!");
    } catch (e) { toast.error("Failed to add contact"); }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      const res = await api.delete(`/leads/${leadId}/contacts/${contactId}`);
      setLead(res.data);
      toast.success("Contact removed");
    } catch (e) { toast.error("Failed"); }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    try {
      const res = await api.post(`/leads/${leadId}/comments`, { text: comment });
      setLead(res.data);
      setComment("");
      toast.success("Note added");
    } catch (e) { toast.error("Failed"); }
  };

  const handleResearch = async () => {
    setResearching(true);
    try {
      await api.post(`/leads/${leadId}/research`);
      // Reload the full lead to get amp_intel embedded
      await loadData();
      toast.success("Research complete!");
    } catch (e) { toast.error("Research failed"); }
    finally { setResearching(false); }
  };

  const handleDeleteLead = async () => {
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead deleted");
      navigate("/bizdev/leads");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  // --- Render ---
  if (loading) return window.innerWidth < 768 ? <MobileSkeleton type="detail" /> : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh", fontFamily: "var(--font-orb)", fontSize: 14, color: "var(--t3)" }}>Loading...</div>;
  if (!lead) return null;

  const stageColor = STAGE_COLORS[lead.funnel_stage] || "#64748B";
  const next = nextStage(lead.funnel_stage);
  const daysIn = daysInStage(lead);
  const contacts = lead.contacts || [];
  const comments = lead.comments || [];
  const intel = lead.research_data || lead.amp_intel || null;
  const activeBriefs = briefs.filter(b => b.status !== "Completed" && b.status !== "Cancelled").length;
  const lastActivity = comments.length > 0 ? comments[comments.length - 1]?.created_at : lead.updated_at;
  const roleKey = (r) => (r || "").toLowerCase();
  const canDelete = ["admin", "srmanager", "manager"].includes(roleKey(user?.role)) || isSuperAdmin(user?.email);

  return (
    <div className="dm-page-container" style={s.page}>
      {/* Back */}
      <button onClick={() => navigate("/bizdev/leads")} style={s.back}>&larr; LEADS</button>

      {/* Header */}
      <div className="dm-detail-header" style={s.headerRow}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            {lead.website && lead.website !== "Unknown" && (
              <img src={`https://www.google.com/s2/favicons?domain=${lead.website.replace(/^https?:\/\//, "").replace(/\/.*$/, "")}&sz=128`}
                width={40} height={40} alt={lead.company_name}
                style={{ borderRadius: 10, objectFit: "contain", background: "var(--s3)" }}
                onError={e => { e.target.style.display = "none"; }} />
            )}
            <h1 style={s.h1}>{lead.company_name}</h1>
          </div>
          <div style={s.meta}>
            {lead.industry && lead.industry !== "Unknown" && <span>{lead.industry}</span>}
            {lead.location && lead.location !== "Unknown" && <span>{lead.location}</span>}
            {lead.website && lead.website !== "Unknown" && (
              <a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer" style={s.metaLink}>{lead.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a>
            )}
          </div>
        </div>
        <div className="dm-detail-actions" style={s.actions}>
          <button onClick={() => navigate(`/bizdev/briefs/new?lead_id=${leadId}`)} style={{ ...s.actionBtn, background: "var(--rd)", borderColor: "rgba(227,25,55,.3)", color: "var(--r)", fontWeight: 700 }}>+ BRIEF</button>
          <button onClick={() => navigate(`/tasks/new?lead_id=${leadId}`)} style={{ ...s.actionBtn, background: "rgba(139,92,246,.08)", borderColor: "rgba(139,92,246,.25)", color: "#A78BFA" }} title="New Task">+ TASK</button>
          <button onClick={startEditLead} style={{ ...s.actionBtn, padding: "5px 8px" }} title="Edit Lead">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
          <div style={{ position: "relative" }}>
            <button onClick={() => setShowMore(!showMore)} style={{ ...s.actionBtn, padding: "5px 8px" }} title="More">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
            </button>
            {showMore && (
              <div style={{ position: "absolute", right: 0, top: "100%", marginTop: 4, background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 8, padding: 4, zIndex: 20, minWidth: 140 }}>
                <button onClick={() => { setShowMore(false); handleResearch(); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", background: "none", border: "none", color: "var(--t2)", fontFamily: "var(--font-mono)", fontSize: 11, cursor: "pointer", borderRadius: 4 }} onMouseEnter={e => e.currentTarget.style.background = "var(--s2)"} onMouseLeave={e => e.currentTarget.style.background = "none"}>Research Lead</button>
                {canDelete && <button onClick={() => { setShowMore(false); handleDeleteLead(); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", background: "none", border: "none", color: "#EF4444", fontFamily: "var(--font-mono)", fontSize: 11, cursor: "pointer", borderRadius: 4 }} onMouseEnter={e => e.currentTarget.style.background = "var(--s2)"} onMouseLeave={e => e.currentTarget.style.background = "none"}>Delete Lead</button>}
              </div>
            )}
          </div>
          <ShareButton title={lead?.company_name} text={`Lead: ${lead?.company_name}`} />
        </div>
      </div>

      {/* Edit Lead Modal */}
      {editingLead && (
        <div onClick={() => setEditingLead(false)} style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,.6)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, width: "100%", maxWidth: 460, margin: "0 16px", padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,.5)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t2)", letterSpacing: ".1em" }}>EDIT LEAD</span>
              <button onClick={() => setEditingLead(false)} style={{ background: "var(--s3)", border: "1px solid var(--bdr)", borderRadius: 6, padding: "4px 10px", color: "var(--t2)", cursor: "pointer", fontSize: 16, lineHeight: 1, transition: "all .15s" }} onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,68,68,.15)"; e.currentTarget.style.color = "#EF4444"; e.currentTarget.style.borderColor = "rgba(239,68,68,.3)"; }} onMouseLeave={e => { e.currentTarget.style.background = "var(--s3)"; e.currentTarget.style.color = "var(--t2)"; e.currentTarget.style.borderColor = "var(--bdr)"; }}>&times;</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", display: "block", marginBottom: 4 }}>COMPANY NAME</label>
                <input style={s.input} value={editLeadForm.company_name} onChange={e => setEditLeadForm({...editLeadForm, company_name: e.target.value})} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", display: "block", marginBottom: 4 }}>INDUSTRY</label>
                  <input style={s.input} value={editLeadForm.industry} onChange={e => setEditLeadForm({...editLeadForm, industry: e.target.value})} />
                </div>
                <div>
                  <label style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", display: "block", marginBottom: 4 }}>LOCATION</label>
                  <input style={s.input} value={editLeadForm.location} onChange={e => setEditLeadForm({...editLeadForm, location: e.target.value})} />
                </div>
              </div>
              <div>
                <label style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", display: "block", marginBottom: 4 }}>WEBSITE</label>
                <input style={s.input} value={editLeadForm.website} onChange={e => setEditLeadForm({...editLeadForm, website: e.target.value})} placeholder="https://..." />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", display: "block", marginBottom: 4 }}>EST. VALUE (LAKHS)</label>
                  <input type="number" style={s.input} value={editLeadForm.estimated_value_lakhs} onChange={e => setEditLeadForm({...editLeadForm, estimated_value_lakhs: e.target.value})} />
                </div>
                <div>
                  <label style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", display: "block", marginBottom: 4 }}>RETAINER</label>
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <button onClick={() => setEditLeadForm({...editLeadForm, is_retainer: true})} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid", borderColor: editLeadForm.is_retainer ? "rgba(6,182,212,.4)" : "var(--bdr)", background: editLeadForm.is_retainer ? "rgba(6,182,212,.1)" : "var(--s2)", color: editLeadForm.is_retainer ? "var(--c)" : "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 11, cursor: "pointer" }}>Yes</button>
                    <button onClick={() => setEditLeadForm({...editLeadForm, is_retainer: false})} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid", borderColor: !editLeadForm.is_retainer ? "rgba(6,182,212,.4)" : "var(--bdr)", background: !editLeadForm.is_retainer ? "rgba(6,182,212,.1)" : "var(--s2)", color: !editLeadForm.is_retainer ? "var(--c)" : "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 11, cursor: "pointer" }}>No</button>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                <button onClick={() => setEditingLead(false)} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--bdr)", background: "var(--s2)", color: "var(--t2)", fontFamily: "var(--font-mono)", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                <button onClick={saveLeadEdit} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: "var(--r)", color: "#fff", fontFamily: "var(--font-mono)", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stage Row */}
      <div className="dm-stage-row" style={s.stageRow}>
        <span style={s.badge(stageColor)}>{lead.funnel_stage}</span>
        {next && (
          <div style={{ position: "relative" }}>
            <button onClick={() => setShowStageDropdown(!showStageDropdown)} style={{ ...s.actionBtn, fontSize: 8 }}>
              Move to &rarr; {next} &#9662;
            </button>
            {showStageDropdown && (
              <div style={{ position: "absolute", left: 0, top: "100%", marginTop: 4, background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 8, padding: 4, zIndex: 20, minWidth: 130 }}>
                {FUNNEL_STAGES.filter(st => st !== lead.funnel_stage).map(st => (
                  <button key={st} onClick={() => handleStageChange(st)} style={{ display: "block", width: "100%", textAlign: "left", padding: "5px 10px", background: "none", border: "none", color: STAGE_COLORS[st] || "var(--t2)", fontFamily: "var(--font-mono)", fontSize: 11, cursor: "pointer", borderRadius: 4 }} onMouseEnter={e => e.currentTarget.style.background = "var(--s2)"} onMouseLeave={e => e.currentTarget.style.background = "none"}>{st}</button>
                ))}
              </div>
            )}
          </div>
        )}
        {lead.ai_score > 0 && <span style={s.badge(lead.ai_score > 70 ? "#22C55E" : lead.ai_score > 40 ? "#F59E0B" : "#EF4444")}>Fitment: {lead.ai_score}</span>}
        {daysIn !== null && <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", padding: "3px 8px", background: "var(--s2)", borderRadius: 20 }}>{daysIn}d in stage</span>}
      </div>

      {/* KPI Cards */}
      <div className="dm-lead-detail-kpi" style={s.kpiGrid}>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>BRIEFS</div>
          <div style={s.kpiValue}>{briefs.length}</div>
          {activeBriefs > 0 && <div style={s.kpiSub}>{activeBriefs} active</div>}
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>EST. VALUE</div>
          <div style={s.kpiValue}>{fmt(lead.cy_revenue_lakhs || lead.estimated_value_lakhs || 0)}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>CONTACTS</div>
          <div style={s.kpiValue}>{contacts.length}</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>LAST ACTIVITY</div>
          <div style={{ ...s.kpiValue, fontSize: 14 }}>{relativeTime(lastActivity)}</div>
        </div>
      </div>

      {/* Contacts Section */}
      <div style={s.section}>
        <div style={s.sectionTitle}>
          <span>CONTACTS</span>
          <button onClick={() => setShowAddContact(true)} style={s.smallBtn}>+ Add Contact</button>
        </div>
        {contacts.length === 0 && <div style={{ ...s.card, textAlign: "center", color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 9 }}>No contacts yet</div>}
        {contacts.map(c => (
          <div key={c.contact_id} style={{ ...s.card, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ ...s.avatar(c.name), position: "relative" }}>
              {(c.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--t1)" }}>{c.name}</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)" }}>{c.designation || ""}</div>
            </div>
            {c.email && <a href={`mailto:${c.email}`} style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--c)", textDecoration: "none" }}>{c.email}</a>}
            {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noreferrer" style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--c)", textDecoration: "none" }}>LinkedIn</a>}
            <button onClick={() => handleDeleteContact(c.contact_id)} style={{ background: "none", border: "none", color: "var(--t3)", cursor: "pointer", fontSize: 12 }} title="Remove">&times;</button>
          </div>
        ))}
      </div>

      {/* Briefs Section */}
      <div style={s.section}>
        <div style={s.sectionTitle}>
          <span>BRIEFS</span>
          <button onClick={() => navigate(`/bizdev/briefs/new?lead_id=${leadId}`)} style={s.smallBtn}>+ New Brief</button>
        </div>
        {briefs.length === 0 && <div style={{ ...s.card, textAlign: "center", color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 9 }}>No briefs yet</div>}
        {briefs.map(b => {
          const statusColors = { Draft: "#64748B", Active: "#22C55E", "In Progress": "#F59E0B", Completed: "#8B5CF6", Cancelled: "#6B7280" };
          const dotColor = statusColors[b.status] || "#64748B";
          return (
            <button key={b.brief_id} onClick={() => navigate(`/bizdev/briefs/${b.brief_id}`)} style={{ ...s.card, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", width: "100%", textAlign: "left" }}>
              <div style={s.dot(dotColor)} />
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 12, color: "var(--t1)", fontWeight: 500 }}>{b.title}</span>
                {b.brief_type && <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", marginLeft: 8 }}>{b.brief_type}</span>}
              </div>
              <span style={s.badge(dotColor)}>{b.status}</span>
            </button>
          );
        })}
      </div>

      {/* Tasks Section */}
      <div style={s.section}>
        <div style={s.sectionTitle}>
          <span>TASKS</span>
          <button onClick={() => navigate(`/tasks/new?lead_id=${leadId}`)} style={s.smallBtn}>+ New Task</button>
        </div>
        {tasks.length === 0 && <div style={{ ...s.card, textAlign: "center", color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 9 }}>No tasks linked to this lead</div>}
        {tasks.map(t => {
          const statusColors = { Open: "#64748B", "In Progress": "#F59E0B", "In Review": "#8B5CF6", Done: "#22C55E", Blocked: "#EF4444" };
          const priorityColors = { High: "#EF4444", Medium: "#F59E0B", Low: "#64748B" };
          const dotColor = statusColors[t.status] || "#64748B";
          return (
            <button key={t.task_id} onClick={() => navigate(`/tasks/${t.task_id}`)} style={{ ...s.card, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", width: "100%", textAlign: "left" }}>
              <div style={s.dot(dotColor)} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12, color: "var(--t1)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{t.title}</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)" }}>
                  {t.assignee_name || "Unassigned"}{t.due_date ? ` · Due ${new Date(t.due_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : ""}
                </span>
              </div>
              {t.priority && <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: priorityColors[t.priority] || "var(--t3)" }}>{t.priority}</span>}
              <span style={s.badge(dotColor)}>{t.status}</span>
            </button>
          );
        })}
      </div>

      {/* AMP Intel Section */}
      <div style={s.section}>
        <div style={s.sectionTitle}>
          <span>AMP INTEL</span>
          <button onClick={handleResearch} disabled={researching} style={{ ...s.smallBtn, color: researching ? "var(--t3)" : "var(--c)" }}>
            {researching ? "Researching..." : "Research"}
          </button>
        </div>
        {researching && (
          <div style={{ ...s.card, textAlign: "center", padding: 24 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--c)", animation: "pulse 1.5s infinite" }}>Analyzing {lead.company_name}...</div>
          </div>
        )}
        {intel && !researching && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* Brand Challenges */}
            {intel.brand_challenges && intel.brand_challenges.length > 0 && (
              <div style={s.card}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", marginBottom: 6, letterSpacing: ".06em" }}>BRAND CHALLENGES</div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {intel.brand_challenges.map((item, i) => <li key={i} style={{ fontSize: 11, color: "var(--t2)", marginBottom: 3 }}>{item}</li>)}
                </ul>
              </div>
            )}
            {/* Recent News */}
            {intel.recent_news && intel.recent_news.length > 0 && (
              <div style={s.card}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", marginBottom: 6, letterSpacing: ".06em" }}>RECENT NEWS</div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {intel.recent_news.map((item, i) => <li key={i} style={{ fontSize: 11, color: "var(--t2)", marginBottom: 3 }}>{typeof item === "string" ? item : item.title || item.headline || JSON.stringify(item)}</li>)}
                </ul>
              </div>
            )}
            {/* Competitor Threats */}
            {intel.competitor_threats && intel.competitor_threats.length > 0 && (
              <div style={s.card}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", marginBottom: 6, letterSpacing: ".06em" }}>COMPETITOR THREATS</div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {intel.competitor_threats.map((item, i) => <li key={i} style={{ fontSize: 11, color: "var(--t2)", marginBottom: 3 }}>{item}</li>)}
                </ul>
              </div>
            )}
            {/* Opportunities */}
            {(intel.opportunities_for_agency || intel.opportunities || []).length > 0 && (
              <div style={s.card}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", marginBottom: 6, letterSpacing: ".06em" }}>OPPORTUNITIES FOR AGENCY</div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {(intel.opportunities_for_agency || intel.opportunities || []).map((item, i) => <li key={i} style={{ fontSize: 11, color: "var(--t2)", marginBottom: 3 }}>{item}</li>)}
                </ul>
              </div>
            )}
            {/* Ready-to-Send Outreach */}
            {(intel.whatsapp_message || intel.email_body || intel.linkedin_message || intel.outreach_messages) && (
              <div style={s.card}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", marginBottom: 8, letterSpacing: ".06em" }}>READY-TO-SEND</div>
                <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
                  {["whatsapp", "email", "linkedin"].map(t => (
                    <button key={t} onClick={() => setOutreachTab(t)} style={s.tab(outreachTab === t)}>{t.toUpperCase()}</button>
                  ))}
                </div>
                {outreachTab === "whatsapp" && (intel.whatsapp_message || intel.outreach_messages?.whatsapp) && (
                  <div>
                    <div style={{ background: "var(--bg)", borderRadius: 8, padding: 12, fontSize: 11, color: "var(--t2)", lineHeight: 1.5, whiteSpace: "pre-wrap", marginBottom: 8 }}>{intel.whatsapp_message || intel.outreach_messages?.whatsapp}</div>
                    <button onClick={() => copyText(intel.whatsapp_message || intel.outreach_messages?.whatsapp)} style={s.copyBtn}>Copy</button>
                  </div>
                )}
                {outreachTab === "email" && (intel.email_body || intel.outreach_messages?.email) && (
                  <div>
                    {intel.email_subject && <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--c)", marginBottom: 6 }}>Subject: {intel.email_subject}</div>}
                    <div style={{ background: "var(--bg)", borderRadius: 8, padding: 12, fontSize: 11, color: "var(--t2)", lineHeight: 1.5, whiteSpace: "pre-wrap", marginBottom: 8 }}>{intel.email_body || intel.outreach_messages?.email}</div>
                    <button onClick={() => copyText(intel.email_body || intel.outreach_messages?.email)} style={s.copyBtn}>Copy</button>
                  </div>
                )}
                {outreachTab === "linkedin" && (intel.linkedin_message || intel.outreach_messages?.linkedin) && (
                  <div>
                    <div style={{ background: "var(--bg)", borderRadius: 8, padding: 12, fontSize: 11, color: "var(--t2)", lineHeight: 1.5, whiteSpace: "pre-wrap", marginBottom: 8 }}>{intel.linkedin_message || intel.outreach_messages?.linkedin}</div>
                    <button onClick={() => copyText(intel.linkedin_message || intel.outreach_messages?.linkedin)} style={s.copyBtn}>Copy</button>
                  </div>
                )}
              </div>
            )}
            {/* Pitch Angles */}
            {intel.pitch_angles && intel.pitch_angles.length > 0 && (
              <div style={s.card}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)", marginBottom: 6, letterSpacing: ".06em" }}>PITCH ANGLES</div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {intel.pitch_angles.map((item, i) => <li key={i} style={{ fontSize: 11, color: "var(--t2)", marginBottom: 3 }}>{item}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        {!intel && !researching && (
          <div style={{ ...s.card, textAlign: "center", color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 11, padding: 20 }}>
            No intel yet. Click "Research" to analyze this lead.
          </div>
        )}
      </div>

      {/* Seasonal Events */}
      {events.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}><span>UPCOMING OPPORTUNITIES</span></div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {events.map((ev, i) => {
              const daysLeft = ev.days_until != null ? ev.days_until : Math.max(0, Math.ceil((new Date(ev.date || ev.start_date).getTime() - Date.now()) / 86400000));
              return (
                <span key={i} style={{ fontFamily: "var(--font-mono)", fontSize: 11, padding: "4px 12px", borderRadius: 20, background: "var(--s2)", border: "1px solid var(--bdr)", color: daysLeft < 30 ? "var(--r)" : "var(--t2)" }}>
                  {ev.name || ev.event_name} ({daysLeft}d)
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Activity Section */}
      <div style={s.section}>
        <div style={s.sectionTitle}><span>ACTIVITY</span></div>
        {/* Comment Input */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input value={comment} onChange={e => setComment(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleComment(); } }} placeholder="Add a note..." style={s.input} />
          <button onClick={handleComment} style={{ ...s.actionBtn, padding: "8px 14px" }}>Post</button>
        </div>
        {/* Timeline */}
        {comments.length === 0 && <div style={{ textAlign: "center", color: "var(--t3)", fontFamily: "var(--font-mono)", fontSize: 11, padding: 16 }}>No activity yet</div>}
        {[...comments].reverse().map((c, i) => (
          <div key={c.comment_id || i} style={{ ...s.card, display: "flex", gap: 10 }} className="group">
            <div style={{ width: 2, background: "var(--bdr)", borderRadius: 2, flexShrink: 0, minHeight: 24 }} />
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t1)", fontWeight: 600 }}>{c.author_name || "System"}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--t3)" }}>{relativeTime(c.created_at)}</span>
                  {c.comment_id && (c.author_id === user?.user_id || c.author_id === user?.id || isSuperAdmin(user)) && (
                    <button onClick={async () => { if (!window.confirm("Delete this comment?")) return; try { const res = await api.delete(`/leads/${leadId}/comments/${c.comment_id}`); setLead(res.data); setComments(res.data.comments || []); toast.success("Comment deleted"); } catch { toast.error("Failed to delete"); } }}
                      style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 12, padding: "2px 6px", borderRadius: 4, transition: "all .15s" }}
                      onMouseEnter={e => { e.currentTarget.style.color = "#EF4444"; e.currentTarget.style.background = "rgba(239,68,68,.1)"; }}
                      onMouseLeave={e => { e.currentTarget.style.color = "#444"; e.currentTarget.style.background = "none"; }}
                      title="Delete comment">✕</button>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 11, color: "var(--t2)", lineHeight: 1.5 }}>{c.text}</div>
            </div>
          </div>
        ))}
      </div>

      {/* AI Insight */}
      {lead.score_reasoning && (
        <div style={s.section}>
          <button onClick={() => setShowInsight(!showInsight)} style={{ ...s.sectionTitle, cursor: "pointer", background: "none", border: "none", padding: 0, width: "100%" }}>
            <span>AI INSIGHT</span>
            <span style={{ fontSize: 12, color: "var(--t3)" }}>{showInsight ? "\u25B2" : "\u25BC"}</span>
          </button>
          {showInsight && (
            <div style={s.card}>
              <div style={{ fontSize: 11, color: "var(--t2)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{lead.score_reasoning}</div>
            </div>
          )}
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddContact && (
        <div style={{ position: "fixed", inset: 0, zIndex: 90, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={() => setShowAddContact(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.7)" }} />
          <form onSubmit={handleAddContact} style={{ position: "relative", background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 14, padding: 24, width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontFamily: "var(--font-orb)", fontSize: 14, color: "var(--t1)" }}>ADD CONTACT</div>
              <button type="button" onClick={() => setShowAddContact(false)} style={{ background: "var(--s3)", border: "1px solid var(--bdr)", borderRadius: 6, padding: "4px 10px", color: "var(--t2)", cursor: "pointer", fontSize: 16, lineHeight: 1, transition: "all .15s" }} onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,68,68,.15)"; e.currentTarget.style.color = "#EF4444"; }} onMouseLeave={e => { e.currentTarget.style.background = "var(--s3)"; e.currentTarget.style.color = "var(--t2)"; }} title="Close">&#10005;</button>
            </div>
            <input value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} placeholder="Name *" required style={s.input} />
            <input value={contactForm.designation} onChange={e => setContactForm({ ...contactForm, designation: e.target.value })} placeholder="Designation" style={s.input} />
            <input value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} placeholder="Email" type="email" style={s.input} />
            <input value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="Phone" style={s.input} />
            <input value={contactForm.linkedin_url} onChange={e => setContactForm({ ...contactForm, linkedin_url: e.target.value })} placeholder="LinkedIn URL" style={s.input} />
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button type="button" onClick={() => setShowAddContact(false)} style={{ flex: 1, padding: 10, background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 8, color: "var(--t2)", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 12 }}>Cancel</button>
              <button type="submit" style={{ flex: 1, padding: 10, background: "var(--r)", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 12, fontWeight: 600 }}>Add</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
